import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Tables } from "@/integrations/supabase/types";
import { useAuth } from "@/components/AuthProvider";

type Drawing = Tables<"whiteboard_history"> & {
  drawing_analyses?: {
    id: string;
    procedure_type: string;
    analysis_text: string;
    created_at: string;
    drawing_id: string;
    key_points: Json | null;
    status: "pending" | "completed" | "failed";
    updated_at: string;
    user_id: string;
  }[];
};

type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export function useDrawings(itemsPerPage: number = 4) {
  const [drawings, setDrawings] = useState<Drawing[]>([]);
  const [filteredDrawings, setFilteredDrawings] = useState<Drawing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const { user } = useAuth();

  // New function to fetch all unique tags
  const fetchAllTags = useCallback(async () => {
    if (!user) return;

    try {
      console.log('Fetching all unique tags for user:', user.id);
      const { data, error } = await supabase
        .from("whiteboard_history")
        .select('tags')
        .eq('user_id', user.id);

      if (error) throw error;

      // Flatten and get unique tags
      const allTags = new Set<string>();
      data.forEach(drawing => {
        drawing.tags?.forEach(tag => allTags.add(tag.toLowerCase()));
      });

      console.log('Retrieved all unique tags:', Array.from(allTags));
      setAvailableTags(Array.from(allTags));
    } catch (error) {
      console.error("Error fetching tags:", error);
      toast({
        variant: "destructive",
        title: "Error loading tags",
        description: "There was a problem loading your tags.",
      });
    }
  }, [user]);

  const fetchDrawings = useCallback(async (page = 0) => {
    if (!user) return;

    try {
      console.log('Fetching drawings for page:', page, 'user:', user.id, 'with tags:', selectedTags);
      const start = page * itemsPerPage;
      const end = start + itemsPerPage - 1;

      let query = supabase
        .from("whiteboard_history")
        .select(`
          id,
          title,
          description,
          preview_image,
          tags,
          created_at,
          updated_at,
          drawing_data,
          featured,
          is_public,
          user_id,
          view_count,
          last_modified_at,
          last_modified_by,
          version,
          drawing_analyses (*)
        `, { count: 'exact' })
        .eq('user_id', user.id)
        .order("created_at", { ascending: false });

      // Apply tag filtering if tags are selected
      if (selectedTags.length > 0) {
        query = query.contains('tags', selectedTags);
      }

      // Apply range for pagination
      query = query.range(start, end);

      const { data, error, count } = await query;

      if (error) throw error;

      console.log('Received drawings:', data?.length);
      
      if (page === 0) {
        setDrawings(data as Drawing[]);
      } else {
        setDrawings(prev => [...prev, ...(data as Drawing[])]);
      }
      
      setHasMore(count ? start + itemsPerPage < count : false);
      
    } catch (error) {
      console.error("Error fetching drawings:", error);
      toast({
        variant: "destructive",
        title: "Error loading drawings",
        description: "There was a problem loading your previous drawings.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [itemsPerPage, selectedTags, user]);

  // Initial fetch of all tags and first page of drawings
  useEffect(() => {
    if (user) {
      console.log('Initial fetch for user:', user.id);
      fetchAllTags();
      fetchDrawings(0);
    }
  }, [fetchDrawings, fetchAllTags, user]);

  // Reset pagination and fetch new results when tags change
  useEffect(() => {
    if (user) {
      console.log('Tags changed, resetting pagination and fetching new results');
      setCurrentPage(0);
      fetchDrawings(0);
    }
  }, [selectedTags, user, fetchDrawings]);

  // Updated filtering logic
  useEffect(() => {
    console.log('Filtering drawings with:', { searchQuery, selectedTags });
    let filtered = drawings;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        drawing =>
          drawing.title?.toLowerCase().includes(query) ||
          drawing.description?.toLowerCase().includes(query)
      );
    }

    console.log('Filtered drawings count:', filtered.length);
    setFilteredDrawings(filtered);
  }, [searchQuery, drawings]);

  const loadMoreDrawings = useCallback(() => {
    setCurrentPage(prev => prev + 1);
    fetchDrawings(currentPage + 1);
  }, [currentPage, fetchDrawings]);

  return {
    drawings: filteredDrawings,
    isLoading,
    hasMore,
    searchQuery,
    setSearchQuery,
    selectedTags,
    setSelectedTags,
    availableTags,
    loadMoreDrawings,
    fetchDrawings
  };
}
